import React, { createContext, useState, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export const ThemeToggleContext = createContext();

// Numeral treatment for mileage/cost readouts -- just tabular-nums so
// figures align in columns; no separate monospace typeface (Inter carries
// the whole app, including numbers).
export const monoFontSx = {
  fontVariantNumeric: "tabular-nums",
};

const fontFamily = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

// Content sections in this app no longer live inside Paper/Card -- they're
// flat Box + Divider (see components). Paper/Card are left at MUI's normal
// elevation-aware defaults on purpose, since they're still the right
// primitive for genuinely floating surfaces (Dialog, Select/Menu popovers,
// Snackbar, the assistant chat popup) which should keep reading as raised
// above the page.
function buildComponentOverrides(palette) {
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: palette.primary.main,
          "&.Mui-checked": {
            color: palette.primary.main,
            "& + .MuiSwitch-track": {
              backgroundColor: palette.primary.main,
              opacity: 0.5,
            },
          },
        },
        track: {
          backgroundColor: palette.text.secondary,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        notchedOutline: {
          borderColor: palette.divider,
        },
        input: {
          color: palette.text.primary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  };
}

const darkPalette = {
  mode: "dark",
  background: {
    default: "#101114",
    paper: "#17181C",
  },
  primary: {
    main: "#6366F1",
    dark: "#4F46E5",
    light: "#818CF8",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#4A4D57",
    contrastText: "#FFFFFF",
  },
  text: {
    primary: "#EDEDEF",
    secondary: "#9A9BA3",
  },
  divider: "rgba(255,255,255,0.08)",
};

const lightPalette = {
  mode: "light",
  background: {
    default: "#FAFAFA",
    paper: "#FFFFFF",
  },
  primary: {
    main: "#4F46E5",
    dark: "#4338CA",
    light: "#6366F1",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#5B5D66",
    contrastText: "#FFFFFF",
  },
  text: {
    primary: "#17181C",
    secondary: "#5B5D66",
  },
  divider: "rgba(10,10,15,0.08)",
};

export const darkTheme = createTheme({
  palette: darkPalette,
  shape: { borderRadius: 8 },
  typography: { fontFamily },
  components: buildComponentOverrides(darkPalette),
});

export const lightTheme = createTheme({
  palette: lightPalette,
  shape: { borderRadius: 8 },
  typography: { fontFamily },
  components: buildComponentOverrides(lightPalette),
});

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState("dark");

  const theme = useMemo(() => (mode === "light" ? lightTheme : darkTheme), [mode]);

  return (
    <ThemeToggleContext.Provider value={{ mode, setMode }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeToggleContext.Provider>
  );
};
